/**
 * TopBar — global header.
 *
 * Layout: [brand] [environment switcher + global search] [theme + alerts + settings + avatar]
 * The brand cell lines up with the sidebar width so the visual divider runs clean.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  HelpCircle,
  Keyboard,
  LogOut,
  Menu,
  Search,
  Settings,
  UserCircle,
} from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/app/brand-mark";
import { EnvSwitcher } from "@/components/app/env-switcher";
import { ApiStatusBadge } from "@/components/app/api-status-badge";
import { NotificationsBell } from "@/components/app/notifications-bell";
import { CommandPalette } from "@/components/app/command-palette";
import { ShortcutHelp } from "@/components/app/shortcut-help";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/stores/auth";

interface TopBarProps {
  onToggleSidebar?: () => void;
  sidebarOpen?: boolean;
}

export function TopBar({ onToggleSidebar, sidebarOpen = false }: TopBarProps) {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  // Global command-palette state. Lives here because the topbar both
  // triggers it (search box click) AND registers the global Cmd/Ctrl+K
  // shortcut. A Zustand store would be overkill for one boolean.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  // Cmd+K (macOS) / Ctrl+K (everywhere else) opens the palette from
  // anywhere in the app. We intentionally don't preventDefault on the
  // browser's own Cmd+K (URL bar shortcut on some browsers) unless the
  // user is focused inside the app — checking event.target prevents
  // hijacking system shortcuts while the user is in browser chrome.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const k = e.key.toLowerCase();
      if (k === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function confirmAndSignOut() {
    setSigningOut(true);
    try {
      await logout();
      setSignOutConfirmOpen(false);
      navigate("/login", { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  // Initials fall back to "?" when no user is loaded yet OR when fullName
  // is missing on the response. Build defensively so a partial user object
  // (e.g. an unexpected gateway response) cannot crash the top bar.
  const initials =
    (user?.fullName ?? "")
      .split(" ")
      .map((s) => s[0] ?? "")
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  return (
    <header
      className={`grid h-[60px] items-center border-b border-line bg-surface/60 backdrop-blur-md ${sidebarOpen ? "grid-cols-[auto_1fr_auto] lg:grid-cols-[252px_1fr_auto]" : "grid-cols-[auto_1fr_auto]"}`}
    >
      {/* Brand cell — width matches the sidebar when open, auto when closed. */}
      <div className="flex h-full items-center gap-2.5 px-3 sm:px-4 lg:border-r lg:border-line lg:px-5">
        {!sidebarOpen && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-fg hover:bg-surface-2 transition-colors"
            aria-label="Open sidebar menu"
          >
            <Menu size={16} />
          </button>
        )}
        {sidebarOpen ? (
          <div className="hidden lg:block">
            <BrandMark size={30} />
          </div>
        ) : (
          <div>
            <BrandMark size={30} />
          </div>
        )}
        <div className="hidden lg:block font-extrabold tracking-tight text-[17px]">
          Opus<span className="ml-1 font-medium text-fg-mute">· v2.0</span>
        </div>
      </div>

      {/* Middle — env switcher + global search. */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-3.5 px-2 sm:px-4 lg:px-5">
        <div className="hidden md:block">
          <EnvSwitcher />
        </div>
        {/* Real palette trigger. Click or Cmd/Ctrl+K opens CommandPalette
            which searches across devices/sites/workflows. The shortcut
            hint is wired through to the same handler — no longer a lie. */}
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          aria-label="Open global search"
          className="flex h-9 min-w-0 max-w-[520px] flex-1 items-center gap-2 overflow-hidden rounded-xl border border-line bg-surface px-3 text-left text-sm text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
        >
          <Search size={14} className="shrink-0" />
          <span className="hidden md:block min-w-0 flex-1 truncate">
            Search devices, sites, workflows...
          </span>
          <span className="hidden sm:inline rounded border border-line bg-surface-tint px-1.5 py-0.5 font-mono text-[10px]">
            ⌘K
          </span>
        </button>
        {/* The palette is mounted here but Radix Dialog portals its content
            to document.body, so it floats above all page chrome regardless
            of where in the tree it sits. Keeping it inside the middle grid
            cell avoids creating an unintended 4th column on the topbar
            grid. */}
        <CommandPalette key={String(paletteOpen)} open={paletteOpen} onOpenChange={setPaletteOpen} />
      </div>

      {/* Right — api status + theme + alerts + help + avatar/sign-in. */}
      <div className="flex items-center gap-1.5 sm:gap-2.5 px-2 sm:px-4 lg:px-5">
        <ApiStatusBadge />
        <ThemeToggle />
        {/* Bell now polls /api/device/statuses/ and shows a real unack count
            with a 5-row preview dropdown. Only renders meaningful UI when
            the user is authed and has a Meraki key — fails quietly otherwise. */}
        <NotificationsBell />
        <button
          type="button"
          onClick={() => setShortcutHelpOpen(true)}
          aria-label="Keyboard shortcuts"
          title="Keyboard shortcuts (?)"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-fg-dim hover:bg-surface-2 hover:text-fg"
        >
          <Keyboard size={16} />
        </button>
        <Link
          to="/help"
          aria-label="Help"
          title="Help"
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-surface text-fg-dim hover:bg-surface-2 hover:text-fg"
        >
          <HelpCircle size={16} />
        </Link>
        {user ? (
          // Authenticated: avatar opens an account menu with logout.
          <DropdownMenu>
            <DropdownMenuTrigger
              className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50"
              aria-label="Open account menu"
            >
              <Avatar initials={initials} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuLabel>
                {user.fullName ?? "Account"}
              </DropdownMenuLabel>
              <div className="px-2.5 pb-2 text-[11px] text-fg-mute">
                {user.email}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <UserCircle size={14} />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profile?tab=settings">
                  <Settings size={14} />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setSignOutConfirmOpen(true)}>
                <LogOut size={14} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // Anonymous: explicit sign-in CTA so the user doesn't have to hunt.
          <Button asChild size="sm" variant="ghost">
            <Link to="/login">Sign in</Link>
          </Button>
        )}
      </div>

      <ShortcutHelp open={shortcutHelpOpen} onOpenChange={setShortcutHelpOpen} />

      <Dialog open={signOutConfirmOpen} onOpenChange={setSignOutConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogTitle>Confirm Sign Out</DialogTitle>
          <DialogDescription>
            Are you sure you want to sign out of Opus?
          </DialogDescription>
          <div className="mt-5 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setSignOutConfirmOpen(false)}
              disabled={signingOut}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => void confirmAndSignOut()}
              disabled={signingOut}
            >
              {signingOut ? "Signing out..." : "Sign out"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
}
